import React, { useState } from "react";
import {
  FaUser,
  FaEnvelope,
  FaLock,
  FaUserShield,
  FaUserCog,
  FaTimes,
  FaSave,
  FaArrowLeft
} from "react-icons/fa";
import Swal from "../../utils/swalCompat";
import { useNavigate } from "react-router-dom";
import { addAdmin } from "../../api/endpoints";
import PageHeader from "../../components/PageHeader";

const AddAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "SUB_ADMIN"
  });

  const [errors, setErrors] = useState({});

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Password validation - simple validation, any password is accepted
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // Role validation
    if (!formData.role) {
      newErrors.role = "Role is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      // Scroll to first error
      const firstError = document.querySelector(".is-invalid");
      if (firstError) {
        firstError.focus();
      }
      return;
    }

    try {
      setLoading(true);

      // Prepare data for API - only 4 fields
      const adminData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        role: formData.role
      };

      console.log("Submitting admin data:", adminData);

      // Call API to add admin
      const response = await addAdmin(adminData);
      console.log("API Response:", response);

      if (response.success) {
        await Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.message || "Admin added successfully",
          timer: 2000,
          showConfirmButton: true
        });

        // Reset form
        setFormData({
          name: "",
          email: "",
          password: "",
          role: "SUB_ADMIN"
        });
        setErrors({});

        // Navigate back to admin list after success
        navigate("/admin-list");
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "Failed to add admin"
        });
      }
    } catch (error) {
      console.error("Error adding admin:", error);
      
      // Handle API errors
      if (error.response?.data?.errors) {
        // Handle validation errors from API
        const apiErrors = {};
        if (Array.isArray(error.response.data.errors)) {
          error.response.data.errors.forEach((err) => {
            apiErrors[err.field] = err.message;
          });
        } else if (typeof error.response.data.errors === 'object') {
          Object.keys(error.response.data.errors).forEach((key) => {
            apiErrors[key] = error.response.data.errors[key];
          });
        }
        setErrors(apiErrors);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to add admin. Please try again."
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (formData.name || formData.email || formData.password) {
      Swal.fire({
        title: "Are you sure?",
        text: "You have unsaved changes. Do you want to leave?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, leave",
        cancelButtonText: "Stay"
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/admin-list");
        }
      });
    } else {
      navigate("/admin-list");
    }
  };

  // Get role badge
  const getRoleBadge = (role) => {
    const roleMap = {
      'SUPER_ADMIN': { color: 'bg-danger', icon: <FaUserShield /> },
      'ADMIN': { color: 'bg-primary', icon: <FaUserCog /> },
      'SUB_ADMIN': { color: 'bg-info', icon: <FaUser /> },
      'OPERATOR': { color: 'bg-success', icon: <FaUser /> }
    };
    const roleData = roleMap[role] || roleMap['OPERATOR'];
    return (
      <span className={`badge ${roleData.color} d-inline-flex align-items-center gap-1`}>
        {roleData.icon} {role}
      </span>
    );
  };

  return (
    <div className="lead-page">
      <PageHeader
        title="Add New Admin"
        subtitle="Create a new admin user account"
        actions={
          <>
            <button
              className="btn btn-outline-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              <FaArrowLeft /> Back to List
            </button>
          </>
        }
      />

      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="row">
              {/* Name */}
              <div className="col-md-6 mb-3">
                <label htmlFor="name" className="form-label">
                  Full Name <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaUser />
                  </span>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    id="name"
                    name="name"
                    placeholder="Enter full name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
              </div>

              {/* Email */}
              <div className="col-md-6 mb-3">
                <label htmlFor="email" className="form-label">
                  Email Address <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaEnvelope />
                  </span>
                  <input
                    type="email"
                    className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                    id="email"
                    name="email"
                    placeholder="Enter email address"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>
              </div>

              {/* Password */}
              <div className="col-md-6 mb-3">
                <label htmlFor="password" className="form-label">
                  Password <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaLock />
                  </span>
                  <input
                    type="password"
                    className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                    id="password"
                    name="password"
                    placeholder="Enter password (min 6 characters)"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.password && (
                    <div className="invalid-feedback">{errors.password}</div>
                  )}
                </div>
                <small className="text-muted">
                  Password must be at least 6 characters
                </small>
              </div>

              {/* Role */}
              <div className="col-md-6 mb-3">
                <label htmlFor="role" className="form-label">
                  Role <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <FaUserShield />
                  </span>
                  <select
                    className={`form-select ${errors.role ? 'is-invalid' : ''}`}
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    <option value="SUB_ADMIN">Sub Admin</option>
                    <option value="ADMIN">Admin</option>
                    <option value="OPERATOR">Operator</option>
                    <option value="SUPER_ADMIN">Super Admin</option>
                  </select>
                  {errors.role && (
                    <div className="invalid-feedback">{errors.role}</div>
                  )}
                </div>
              </div>

              {/* Form Actions */}
              <div className="col-12">
                <hr />
                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        Create Admin
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    <FaTimes className="me-2" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Current Form Data Preview */}
      <div className="card mt-3">
        <div className="card-body">
          <h6 className="mb-2">Form Preview:</h6>
          <div className="row">
            <div className="col-md-3">
              <strong>Name:</strong> {formData.name || "Not set"}
            </div>
            <div className="col-md-3">
              <strong>Email:</strong> {formData.email || "Not set"}
            </div>
            <div className="col-md-3">
              <strong>Password:</strong> {formData.password ? "••••••" : "Not set"}
            </div>
            <div className="col-md-3">
              <strong>Role:</strong> {formData.role ? getRoleBadge(formData.role) : "Not set"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAdmin;